import _ from 'lodash';
import CacheableDataObject from './CacheableDataObject';
import moment from 'moment';
import Transaction from './Transaction';
import CategoryColor from '../utilities/CategoryColor';
import DateHelper from '../utilities/DateHelper';
import MultiBudget from './MultiBudget';
import CategoryGroup from "./CategoryGroup";
import Budget from "./Budget";
import { getCategoryGroupsById } from "../utilities/getCategoryGroupsByType.js";

export default class Category extends CacheableDataObject {
	static types = {
		INCOME: 2,
		EXPENSE: 1,
		LOANS: 4,
		SAVINGS: 3,
	};

	static typesNames = {
		1: 'Expenses',
		2: 'Income',
		3: 'Savings',
		4: 'Loans',
	};

	beforeConstruct() {
		this.instanceNumber = --Category.instanceCount;
		this.budgets = {};
		this.children = {};
		this.parents = {};
		this.transactionFinder = null;

		this.show_when_not_used = true;
		this.is_loan = false;
		this.down_payments = [];
		this.original = null;

		this.transactions = {};
		this.deleteEdit = null;
	}

	setTransactionFinder(transactionFinder) {
		this.transactionFinder = transactionFinder;
	}

	_getCategoryGroup() {
		const categoryGroups = getCategoryGroupsById();
		const categoryGroup = categoryGroups[this.category_group_id];
		return categoryGroup || null;
	}

	isIncomeCategory() {
		const categoryGroup = this._getCategoryGroup();
		if(categoryGroup) {
			return categoryGroup.isIncomeCategoryGroup();
		}
		return true;
	}

	isExpenseCategory() {
		const categoryGroup = this._getCategoryGroup();
		if(categoryGroup) {
			return categoryGroup.isExpenseCategoryGroup();
		}
		return true;
	}

	/**
	 * A loan has a balance owed, counts toward the debt totals and is drawn on the payoff graph.
	 * That is the category's own `is_loan` flag, or its group being a Loans group. The group only
	 * decides where the money is budgeted, so a car loan kept under Transportation is still a loan.
	 *
	 * @param categoryGroup the category's group, when the caller has it; read from the store if not
	 */
	isLoanCategory(categoryGroup = this._getCategoryGroup()) {
		return !!this.is_loan || !!categoryGroup?.isLoanCategoryGroup();
	}

	clone() {
		const category = Category.create();
		category.id = this.id;
		category.parents = this.parents;
		category.deleted = this.deleted;
		category.transactionFinder = this.transactionFinder;
		category.transactions = this.transactions;
		category.children = this.children;
		category.budgets = this.budgets;

		category.name = this.name;
		// The budget list draws its rows from these clones rather than from the categories
		// themselves, so a field left out here is a field that page cannot see. `color` arrives
		// from the server and `getColor()` falls back to a neutral grey without it, which is what
		// turned every name on the budget list grey while the ledger showed the same categories
		// coloured. Nothing in `hasChanges()` reads it, so carrying it across cannot dirty a row.
		category.color = this.color;
		category.category_group_id = this.category_group_id;
		category.parent_category_id = this.parent_category_id;
		category.show_when_not_used = this.show_when_not_used;
		category.is_master_budget_for_children = this.is_master_budget_for_children;
		category.is_loan = this.is_loan;
		category.down_payments = this.down_payments;
		category.original = this;
		return category;
	}

	getChildren() {
		const children = [];
		_.each(this.children, (child) => {
			children.push(child);
		});
		return children;
	}

	hasChildren() {
		return !_.isEmpty(this.children);
	}

	hasMasterBudgetParent() {
		return !!this.getParent()?.is_master_budget_for_children;
	}

	static getUncategorizedTotals(selectedDates) {
		const uncategorized = transactionFinder.getUncategorizedTransactions().byDates(selectedDates);
		let uncategorizedIncome = 0;
		let uncategorizedSpent = 0;
		for(let transaction of uncategorized) {
			if(transaction.isAnExpenseBasedOnAmountAndCategory()) {
				uncategorizedSpent += transaction.amount;
			} else {
				uncategorizedIncome -= transaction.amount;
			}
		}
		return { uncategorizedIncome, uncategorizedSpent };
	}

	static getSummaryTotals(categories, categoryGroups, selectedDates, filterDate, filterBy) {
		if(!categories) {
			return {};
		}
		let totalSpent = 0;
		let totalMade = 0;
		let totalSaved = 0;
		let totalSpentOnLoans = 0;
		let totalBudgeted = 0;
		let totalDebt = 0;

		let totalBudgetedOnIncome = 0;
		let totalRemainingToBudget = 0;

		if(Array.isArray(categories)) {
			categories = new Map(categories.map((category) => {
				return [category.id, category];
			}));
		}

		if(categoryGroups instanceof CategoryGroup) {
			categoryGroups = [categoryGroups];
		}
		if(Array.isArray(categoryGroups)) {
			categoryGroups = new Map(categoryGroups.map((categoryGroup) => {
				return [categoryGroups.id, categoryGroup];
			}));
		}

		const countAsExpenseBudgets = {
			[Category.types.SAVINGS]: true,
			[Category.types.EXPENSE]: true,
			[Category.types.LOANS]: true,
		};

		const countAsIncomeBudgets = {
			[Category.types.INCOME]: true,
		};

		for(let [_, category] of categories) {
			const budget = category.getMultiBudget(selectedDates);
			const categoryGroup = categoryGroups.get(category.category_group_id);
			const amountBudgetedInThisCategory = budget.getBudgeted();
			const amountSpentInThisCategory = budget.getSpent(filterDate, filterBy);


			if(categoryGroup) {
				if(countAsExpenseBudgets[categoryGroup.type]) {
					totalBudgeted += amountBudgetedInThisCategory;
					totalRemainingToBudget -= amountBudgetedInThisCategory;
				}
				if(countAsIncomeBudgets[categoryGroup.type]) {
					totalRemainingToBudget += amountBudgetedInThisCategory;
				}
				switch(categoryGroup.type) {
					case Category.types.EXPENSE: {
						totalSpent += amountSpentInThisCategory;
						// A loan budgeted as an expense, a car loan under Transportation, is counted
						// by cash flow: its payment is spending, and is also reported as a loan
						// payment. Nothing adds totalSpentOnLoans to totalSpent, so that is not twice.
						if(category.isLoanCategory(categoryGroup)) {
							totalSpentOnLoans += amountSpentInThisCategory;
							totalDebt += budget.getFundTotal();
						}
						break;
					}
					case Category.types.INCOME: {
						totalBudgetedOnIncome += amountBudgetedInThisCategory;
						totalMade += amountSpentInThisCategory;
						break;
					}
					case Category.types.SAVINGS: {
						totalSaved += amountSpentInThisCategory;
						break;
					}
					case Category.types.LOANS: {
						totalSpentOnLoans += amountSpentInThisCategory;
						totalDebt += budget.getFundTotal();
						break;
					}
					default: {
						console.log('whaaa?!, categoryGroupType', categoryGroup.type);
						console.log('budget', budget);
					}
				}
			} else {
			}
		}

		const { uncategorizedSpent, uncategorizedIncome } = Category.getUncategorizedTotals(selectedDates);
		totalSpent += uncategorizedSpent;
		totalMade += uncategorizedIncome;


		// Should also add uncategorized transactions...
		return {
			totalSpent,
			totalMade,
			totalBudgetedOnIncome,
			totalSaved,
			totalSpentOnLoans,
			totalBudgeted,
			totalRemainingToBudget,
			totalDebt
		};
	}

	static getTypes() {
		const theTypes = [];
		_.each(Category.types, (key, value) => {
			theTypes.push({
				id: key,
				name: Category.typesNames[key]
			});
		});
		return theTypes;
	}

	hasChanges() {
		if(!this.original) {
			return false;
		}
		return this.name !== this.original.name ||
			this.parent_category_id !== this.original.parent_category_id ||
			this.show_when_not_used !== this.original.show_when_not_used ||
			this.is_master_budget_for_children !== this.original.is_master_budget_for_children
			;
	}

	fullName() {
		const parent = this.getParent();
		return `${parent ? `${parent.fullName()} -> ` : ''}${this.name}`;
	}

	getParent() {
		let theParent = null;
		for(let parentKey in this.parents) {
			if(this.parents.hasOwnProperty(parentKey)) {
				theParent = this.parents[parentKey];
				break;
			}
		}
		return theParent;
	}

	/**
	 * The colour the server derived from this category's name, adjusted for where it is drawn.
	 *
	 * `color` is absent on a category built in the browser and not yet saved; it arrives with the
	 * server's answer. CategoryColor falls back to a neutral grey until then rather than deriving
	 * the hue here, which would be a second copy of the hash in `Category_Color` (php) and free to
	 * drift from it.
	 */
	getColor() {
		return CategoryColor.asText(this.color || CategoryColor.NEUTRAL);
	}

	/** As a filled cell -- pale enough that {@link CategoryColor.FILL_TEXT_COLOR} reads over it. */
	getFillColor() {
		return CategoryColor.asFill(this.color || CategoryColor.NEUTRAL);
	}

	getColorRgba(opacity) {
		return CategoryColor.asTextRgba(this.color || CategoryColor.NEUTRAL, opacity);
	}

	addChildCategory(child) {
		this.children[child.id] = child;
	}

	addParentCategory(parent) {
		this.parents[parent.id] = parent;
	}

	addBudget(budget) {
		if(budget.category_id !== this.id) {
			throw new Error('category id to budget mismatch!');
		}
		const budgetKey = `${budget.year}-${budget.month}`;
		budget.update({
			category: this
		});
		this.budgets[budgetKey] = budget;
	}

	/**
	 * THIS WILL NORMALLY BE USED INTERNALLY BY BUDGETS/MULTIBUDGETS
	 * use budget.getTransactions() to retrieve general transactions
	 */
	getTransactionsForBudget(budget) {
		const transactions = [];
		// if(this.is_master_budget_for_children) {
		// 	_.each(this.children, (childCategory) => {
		// 		const childTransactions = childCategory.getTransactionsForBudget(budget);
		// 		for(let childTransaction of childTransactions) {
		// 			transactions.push(childTransaction);
		// 		}
		// 	});
		// }

		this.transactionFinder.getCategoryTransactions(this.id).byMonth(budget.year, budget.month)
			.forEach((transaction) => {
				if(transaction.deleted) {
					return;
				}
				transactions.push(transaction);
			});
		return transactions;
	}

	getMultiBudget(selectedDates) {
		const budgets = [];
		if(moment.isMoment(selectedDates)) {
			selectedDates = [selectedDates];
		}
		_.each(selectedDates, (date) => {
			budgets.push(this.getBudget(date));
		});
		const budget = MultiBudget.create();
		budget.setBudgets(budgets);
		return budget;
	}

	getBudgetsInOrder() {
		let sorted = Object.keys(this.budgets).sort((a, b) => {
			return this.budgets[a].date.isAfter(this.budgets[b].date) ? 1 : -1;
		}).map((sortedKey) => {
			return this.budgets[sortedKey];
		});

		return sorted;
	}

	_lastKnownLoanBudgetBeforeDate(selectedDate) {
		const budgets = this.getUniqueBudgetsInReverseChronologicalOrder()
			.filter((budget) => {
				return budget.date.isBefore(selectedDate);
			});
		for(let budget of budgets) {
			if(budget.hasLoanData()) {
				return budget;
			}
		}
		return null;
	}

	lastKnownInterestAmountBeforeDate(selectedDate) {
		const budget = this._lastKnownLoanBudgetBeforeDate(selectedDate);
		return budget?.interest_amount || null;
	}

	lastKnownMinimumPaymentBeforeDate(selectedDate) {
		const budget = this._lastKnownLoanBudgetBeforeDate(selectedDate);
		return budget?.minimum_payment || null;
	}

	lastKnownBalanceBeforeDate(selectedDate) {
		const budget = this._lastKnownLoanBudgetBeforeDate(selectedDate);
		return budget?.fund_total || null;
	}

	getBudget(selectedDate) {
		if(!selectedDate) {
			throw new Error('need to know which budget to retrieve!');
		}
		const budgetKey = DateHelper.yearMonthSerialize(selectedDate);
		if(this.budgets[budgetKey]) {
			return this.budgets[budgetKey];
		} else {
			return this.budgets[budgetKey] = Budget.create({
				year: selectedDate.year(),
				month: selectedDate.month() + 1,
				category: this,
			});
		}
	}


	/**
	 * Budgets in Chronological order
	 * @returns {Budget[]}
	 */
	getSortedBudgets() {
		let sorted = Object.keys(this.budgets).sort((a, b) => {
			return this.budgets[a].date.isAfter(this.budgets[b].date) ? 1 : -1;
		}).map((sortedKey) => {
			return this.budgets[sortedKey];
		});
		return sorted;
	}

	getBudgetsRelevantToRollbackPeriod(selectedDate) {
		let sorted = this.getSortedBudgets();
		let relevantBudgets = [];
		let foundRolloverStart = false;
		for(let budget of sorted) {
			if(budget.date.isAfter(selectedDate, 'month')) {
				break;
			}
			if(budget.isRolloverStart()) {
				foundRolloverStart = true;
			}
			if(budget.isRolloverEnd()) {
				foundRolloverStart = false;
				relevantBudgets = [];
			}
			if(foundRolloverStart) {
				relevantBudgets.push(budget);
			}
		}
		return relevantBudgets;
	}

	getUniqueBudgetsInReverseChronologicalOrder() {
		let sorted = this.getSortedBudgets().reverse();

		const valuesUsed = {};
		sorted = sorted.filter((budget) => {
			if(!budget.amount && !budget.fund_total && !budget.minimum_payment && !budget.interest_amount) {
				return false;
			}
			const fullValue = `${budget.amount}-${budget.fund_total}-${budget.minimum_payment}-${budget.interest_amount}`;
			if(valuesUsed[budget.amount] === fullValue) {
				return false;
			}
			valuesUsed[budget.amount] = fullValue;
			return true;
		});
		return sorted;
	}

	isDebugCategory(){
		return this.name === 'Daycare';
	}

}
Category.instanceCount = -1;
Category.prototype.dataLocationParsers = {
	type: (category, data) => {
		category.type = Number.parseInt(data.type);
	},
	name: (category, data) => {
		category.name = data.name;
	},
	parent_category_id: (category, data) => {
		category.parent_category_id = data.parent_category_id;
	},
	is_master_budget_for_children: function(category, data) {
		this.is_master_budget_for_children = data.is_master_budget_for_children;
	},
};

export const unknownCategory = Category.create({
	name: 'Unknown',
	id: 0,
});